import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import CueAvatar from '@/assets/cue-avatar.png';
import { PencilEditIcon } from '@/components/icons/pencil-edit';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tag } from '@/components/ui/tag';
import { Typography } from '@/components/ui/typography';
import { useAuthContext } from '@/contexts/auth-context';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import { useIsFirstJobPostOnAccount } from '@/hooks/use-is-first-job-post-on-account';
import { usePostHog } from '@/hooks/use-posthog';
import { cn } from '@/utils/classnames';
import { CAMPAIGNS_POST_JOB_CUE_MESSAGE } from '@/utils/cue-handoff';
import { openCueAndSend } from '@/utils/open-cue-and-send';

const CUE_MOUNT_ID = 'wx-copilot-root';

export function PostJobButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const { postJobPath } = useNavigationPaths();
  const { canPostToJobBoards } = useAuthContext();
  const { startPostJobFlow } = useJobPostingContext();
  const posthog = usePostHog();
  const isFirstJobPostOnAccount = useIsFirstJobPostOnAccount();
  const [open, setOpen] = useState(false);

  const handlePostWithCue = useCallback(() => {
    posthog.capture('source_post_job:button_click', {
      entry_point: 'campaigns',
      is_first_job_post_on_account: isFirstJobPostOnAccount,
    });
    posthog.capture('source_post_job:flow_start', {
      entry_point: 'campaigns',
      post_mode: 'Cue',
      is_first_job_post_on_account: isFirstJobPostOnAccount,
    });
    openCueAndSend(CAMPAIGNS_POST_JOB_CUE_MESSAGE, { mountId: CUE_MOUNT_ID });
    setOpen(false);
  }, [isFirstJobPostOnAccount, posthog]);

  const handlePostManually = useCallback(() => {
    posthog.capture('source_post_job:button_click', {
      entry_point: 'campaigns',
      is_first_job_post_on_account: isFirstJobPostOnAccount,
    });
    startPostJobFlow({ entryPoint: 'campaigns' });
    setOpen(false);
    navigate(`${postJobPath}?fresh=1`, {
      state: { returnTo: `${location.pathname}${location.search}` },
    });
  }, [
    isFirstJobPostOnAccount,
    location.pathname,
    location.search,
    navigate,
    postJobPath,
    posthog,
    startPostJobFlow,
  ]);

  if (!canPostToJobBoards) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="default"
          className="h-10 shrink-0"
          aria-haspopup="menu"
          aria-expanded={open}
          data-testid="campaigns-post-job"
        >
          <FormattedMessage
            defaultMessage="Post job"
            description="Verb phrase: start posting an opening to job boards"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        role="menu"
        className="border-fx-neutral-200 flex w-[302px] max-w-[calc(100vw-32px)] flex-col gap-2 rounded-lg p-3 shadow-lg"
        data-testid="campaigns-post-job-popover"
      >
        <div className="flex flex-col">
          <Typography variant="body2" className="text-fx-neutral-900">
            <FormattedMessage
              defaultMessage="How do you want to post?"
              description="Title asking the recruiter to choose Cue or a manual job-board campaign"
            />
          </Typography>
          <Typography variant="small1" className="text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Cue can handle it, or set it up yourself."
              description="Subtitle explaining Cue versus a self-serve campaign"
            />
          </Typography>
        </div>

        <button
          type="button"
          role="menuitem"
          className={cn(
            'flex w-full items-start gap-3 rounded-lg p-2 text-left',
            'bg-gradient-to-t from-[rgba(95,88,232,0.12)] to-[rgba(40,194,243,0.12)]',
            'hover:from-[rgba(95,88,232,0.18)] hover:to-[rgba(40,194,243,0.18)]',
            'focus-visible:from-[rgba(95,88,232,0.18)] focus-visible:to-[rgba(40,194,243,0.18)]',
            'cursor-pointer outline-none',
          )}
          onClick={handlePostWithCue}
          data-testid="post-with-cue-option"
        >
          <img
            src={CueAvatar}
            alt=""
            width={32}
            height={32}
            className="size-8 shrink-0 rounded-full object-cover"
          />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="flex items-center justify-between gap-2">
              <Typography
                variant="body2"
                className="text-fx-neutral-900 whitespace-nowrap"
              >
                <FormattedMessage
                  defaultMessage="Post with Cue"
                  description="Option to create a job-board campaign with Cue"
                />
              </Typography>
              <Tag
                className="bg-fx-brand-100 h-6"
                textClassName="text-fx-brand-800"
                text={
                  <FormattedMessage
                    defaultMessage="Recommended"
                    description="Badge labeling Post with Cue as the recommended option"
                  />
                }
              />
            </div>
            <Typography variant="small1" className="text-fx-neutral-400">
              <FormattedMessage
                defaultMessage="Create optimal campaigns based on past data & best practices. Post to multiple openings in a single click."
                description="Description of asking Cue to create campaigns for one or more openings"
              />
            </Typography>
          </div>
        </button>

        <button
          type="button"
          role="menuitem"
          className={cn(
            'flex w-full items-start gap-3 rounded-lg p-2 text-left',
            'hover:bg-fx-neutral-100 focus-visible:bg-fx-neutral-100',
            'cursor-pointer outline-none',
          )}
          onClick={handlePostManually}
          data-testid="post-manually-option"
        >
          <span
            className="bg-fx-neutral-100 flex size-8 shrink-0 items-center justify-center rounded-lg p-2"
            aria-hidden="true"
          >
            <PencilEditIcon className="text-fx-neutral-600" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <Typography
              variant="body2"
              className="text-fx-neutral-900 whitespace-nowrap"
            >
              <FormattedMessage
                defaultMessage="Post manually"
                description="Option to open the self-serve job posting flow"
              />
            </Typography>
            <Typography variant="small1" className="text-fx-neutral-400">
              <FormattedMessage
                defaultMessage="Select channels, set your budget, and post yourself — one opening at a time."
                description="Description of posting one opening to job boards without Cue"
              />
            </Typography>
          </div>
        </button>
      </PopoverContent>
    </Popover>
  );
}
